import React from 'react';
import PhoneInput from 'react-phone-number-input/input';
import FieldLevelHelp from '../components/FieldLevelHelp';
import { isCountryUk } from '../util/gearOrderUtil';

/**
 * Email, Cellphone, and Marketing Opt-In form fields for the patient or responsible party.
 *
 * @param {Object} patientInfo - The GearPatient data for the order.
 * @param {Object} form - The form state for the patient info screen.
 * @param {boolean} ukOrder - If the order is a UK order (has a UK client).
 * @param {string} country - The selected country. Falls back to the client's office country, or 'United States' if not available.
 * @param {string} userType - The type of user placing the order (e.g., 'prc' or 'myGDX').
 * @param {boolean} fullRegistrationRequired
 * @param {function} handlePatientInfoChange - The function to call when a patient info field is changed.
 * @param {function} handleShippingChange - The function to call when a shipping-related field is changed (e.g., phone number).
 * @returns {JSX.Element}
 */
const PatientContact = ({
	patientInfo,
	form,
	ukOrder,
	country,
	userType,
	fullRegistrationRequired,
	handlePatientInfoChange,
	handleShippingChange
}) => {
	return (
		<>
			<div className="row">
				<div className={'form-group col-md-4 ' + (form.email.valid === false ? 'has-error' : '')}>
					<div>
						<label className="control-label" htmlFor="patient-email">
							Email Address
							<span className={'text-danger ' + (fullRegistrationRequired ? '' : 'hidden')}>*</span>
						</label>
						<input
							className="form-control patientInfo"
							name="email"
							id="patient-email"
							maxLength="100"
							type="email"
							defaultValue={patientInfo.email ? patientInfo.email : ''}
							autoComplete={userType === 'prc' ? 'email' : 'new-password'}
							onChange={(e) => handlePatientInfoChange('email', e)}
						></input>
						{form.email.valid === false && (
							<span className="validation-message text-danger">{form.email.message}</span>
						)}
					</div>
				</div>
				<div className={'form-group col-md-4 ' + (form.voicePhone.valid === false ? 'has-error' : '')}>
					<label className="control-label" htmlFor="patient-phone">
						{ukOrder ? 'Mobile Phone' : 'Cell Phone'}
						<span className={'text-danger ' + (fullRegistrationRequired ? '' : 'hidden')}>*</span>
						{!ukOrder && <FieldLevelHelp
							html="true"
							helpText={`By submitting your ${ukOrder ? 'mobile' : 'cell'} phone number, you agree to receive automated customer service text messages (e.g., billing and order updates) from Genova Diagnostics. 
														Message frequency varies. Reply HELP for help and STOP to cancel. Message and data rates may apply. View <a target='_blank' href='./terms-and-conditions'>Terms and Conditions</a> and 
														<a target='_blank' href='./privacy'>Privacy</a> policies.`}
							linkText="why we ask"
						/>}
					</label>
					<PhoneInput
						defaultCountry={isCountryUk(country) ? 'GB' : 'US'}
						value={form.voicePhone?.value}
						onChange={(e) => handleShippingChange('voicePhone', e)}
						className="form-control"
						name="tel-national"
						id="patient-phone"
						autoComplete={userType === 'prc' ? 'tel-national' : 'new-password'}
					/>
					{form.voicePhone.valid === false && (
						<span className="validation-message text-danger">{form.voicePhone.message}</span>
					)}
				</div>
				<div className="form-group col-md-4">
					<label className="control-label" htmlFor="patient-alternate-phone">
						Alternate Phone
					</label>
					<PhoneInput
						defaultCountry="US"
						value={form.voicalternatePhoneePhone?.value}
						onChange={(e) => handleShippingChange('alternatePhone', e)}
						className="form-control"
						autoComplete="new-password"
						id="patient-alternate-phone"
					/>
				</div>
			</div>
			{userType === 'prc' && (
				<div className="row">
					<div className="form-group col-12">
						<h4>Communication Preferences</h4>
						<div className="form-check">
							<input
								className="patientInfo form-check-input"
								type="checkbox"
								name="patientInfo"
								id="patient-marketingOptIn"
								defaultChecked={patientInfo.marketingOptIn}
							/>
							<label className="form-check-label fw-normal" htmlFor="patient-marketingOptIn">
								<small>
									I would like to receive relevant reminders and occasional educational content from
									Genova's marketing department.
								</small>
							</label>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PatientContact;
